import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { Tag, Clock, ChevronRight, User, ArrowLeft } from "lucide-react";
import { formatTimeAgo, getCategoryColor } from "./utils";
import { DiscussionCardSkeleton } from "./ForumSkeleton";

export const RecentPostsFeed = ({ posts = [], loading = false, onBackToCategories }) => {
  const theme = useTheme();
  const isDark = theme.mode === "dark";
  const navigate = useNavigate();

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <DiscussionCardSkeleton count={5} />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px 24px",
          color: isDark ? "#64748b" : "#94a3b8",
          backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
          borderRadius: "16px",
          border: `1px dashed ${isDark ? "rgba(255, 255, 255, 0.2)" : "#cbd5e1"}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            backgroundColor: "rgba(236, 178, 54, 0.12)",
            border: "1px solid rgba(236, 178, 54, 0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ECB236",
          }}
        >
          <Clock size={32} />
        </div>
        <div>
          <h3
            style={{
              color: "#ffffff",
              fontSize: "1.25rem",
              fontWeight: 700,
              margin: "0 0 6px 0",
            }}
          >
            No hay publicaciones recientes
          </h3>
          <p
            style={{
              color: isDark ? "#94a3b8" : "#64748b",
              fontSize: "var(--font-size-body, 16px)",
              margin: 0,
            }}
          >
            Aún no se han registrado mensajes en el foro.
          </p>
        </div>
        {onBackToCategories && (
          <button
            type="button"
            onClick={onBackToCategories}
            style={{
              marginTop: "8px",
              padding: "10px 20px",
              borderRadius: "10px",
              border: "1px solid rgba(236, 178, 54, 0.4)",
              backgroundColor: "transparent",
              color: "#ECB236",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(236, 178, 54, 0.1)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <ArrowLeft size={18} />
            Volver a categorías
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px", fontFamily: "'Montserrat', sans-serif" }}>
      {posts.map((post) => {
        const catColor = getCategoryColor(post.category_name || "general");
        return (
          <div
            key={post.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/foro/discussion/${post.discussion_id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/foro/discussion/${post.discussion_id}`);
              }
            }}
            style={{
              backgroundColor: isDark ? "rgba(17, 18, 31, 0.85)" : (theme.colors.cardBackground || "#ffffff"),
              border: "1px solid rgba(255, 255, 255, 0.14)",
              borderRadius: "14px",
              padding: "20px 24px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "20px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
              e.currentTarget.style.borderColor = "#ECB236";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.14)";
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Discussion title */}
              <h3
                style={{
                  color: "#ffffff",
                  margin: 0,
                  fontSize: "var(--font-size-md, 18px)",
                  fontWeight: 700,
                  lineHeight: 1.35,
                }}
              >
                {post.discussion_title}
              </h3>

              {/* Meta row */}
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                {/* Author */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  {post.author_picture ? (
                    <img
                      src={post.author_picture}
                      alt={post.author_name}
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <User size={16} style={{ color: "#94a3b8" }} />
                  )}
                  <span style={{ color: "#cbd5e1", fontSize: "var(--font-size-min, 15px)", fontWeight: 500 }}>
                    {post.author_name}
                  </span>
                </div>

                {/* Category tag */}
                {post.category_name && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      backgroundColor: `${catColor}1a`,
                      border: `1px solid ${catColor}50`,
                      borderRadius: "6px",
                      padding: "3px 8px",
                    }}
                  >
                    <Tag size={12} style={{ color: catColor }} />
                    <span style={{ color: catColor, fontSize: "13px", fontWeight: 600 }}>
                      {post.category_name}
                    </span>
                  </div>
                )}

                {/* Date */}
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <Clock size={14} style={{ color: "#94a3b8" }} />
                  <span style={{ color: "#94a3b8", fontSize: "var(--font-size-min, 15px)" }}>
                    {formatTimeAgo(post.created_at)}
                  </span>
                </div>
              </div>
            </div>

            {/* Trailing arrow */}
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "10px",
                backgroundColor: "rgba(236, 178, 54, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ECB236",
                flexShrink: 0,
              }}
            >
              <ChevronRight size={20} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
